import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Activity, Route } from 'lucide-react';
import originTerminalImage from '../../../assets/landing/generated/slider-origin-terminal.png';
import warehouseImage from '../../../assets/landing/generated/slider-warehouse-fulfillment.png';
import destinationPortImage from '../../../assets/landing/generated/slider-destination-port.png';

function ChevronLeft() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function CountryFlag({ country }) {
  if (country === 'cn') return <svg className="country-flag" viewBox="0 0 30 20" aria-label="China flag" role="img"><rect width="30" height="20" fill="#de2910" /><path fill="#ffde00" d="M6 2.2l.8 2.2 2.4.1-1.9 1.4.7 2.3L6 6.8 4 8.2l.7-2.3-1.9-1.4 2.4-.1L6 2.2z" /></svg>;
  if (country === 'tr') return <svg className="country-flag" viewBox="0 0 30 20" aria-label="Turkey flag" role="img"><rect width="30" height="20" fill="#e30a17" /><circle cx="12" cy="10" r="5" fill="#fff" /><circle cx="14" cy="10" r="4" fill="#e30a17" /><path fill="#fff" d="M19 6.5l.9 2.4 2.5.1-2 1.5.7 2.4-2.1-1.4-2.1 1.4.7-2.4-2-1.5 2.5-.1L19 6.5z" /></svg>;
  return <svg className="country-flag" viewBox="0 0 30 20" aria-label="Ghana flag" role="img"><rect width="30" height="6.67" fill="#ce1126" /><rect y="6.67" width="30" height="6.66" fill="#fcd116" /><rect y="13.33" width="30" height="6.67" fill="#006b3f" /><path fill="#000" d="M15 7.4l.8 2.1H18l-1.8 1.3.7 2.1-1.9-1.3-1.9 1.3.7-2.1-1.8-1.3h2.2L15 7.4z" /></svg>;
}

function makeSlides(promos) {
  const promo = promos[0];

  return [
    {
      tag: 'China | Turkey | Ghana',
      image: originTerminalImage,
      titleTop: 'SHIP FROM',
      titleAccent: 'CHINA OR TURKEY',
      titleBottom: 'STRAIGHT TO YOUR DOOR IN GHANA',
      description: 'Get your personal warehouse address, send packages there, and we handle everything else: tracking, fees, and delivery updates in one place.',
      primary: { label: 'Create Free Account', to: '/register' },
      secondary: { label: 'Track a Package', href: '#tracking' },
      graphic: 'SHIP',
    },
    {
      tag: 'Simple & Transparent',
      image: warehouseImage,
      titleTop: 'TRACK IT.',
      titleAccent: 'PAY IT.',
      titleBottom: 'GET IT DELIVERED.',
      description: 'Follow your items at every step, pay shipping fees online, download your receipt, and chat with our team from one dashboard.',
      primary: { label: 'Sign In', to: '/login' },
      secondary: { label: 'See How It Works', href: '#services' },
      graphic: 'EASY',
    },
    {
      tag: promo ? 'Active Promotion' : 'Rewards & Savings',
      image: destinationPortImage,
      titleTop: promo ? promo.title.toUpperCase() : 'SAVE MORE,',
      titleAccent: promo ? `${promo.discount_percent}% OFF` : 'EARN POINTS,',
      titleBottom: promo ? 'AVAILABLE IN THE SYSTEM NOW' : 'REFER YOUR FRIENDS',
      description: promo
        ? promo.description
        : 'Earn loyalty points on every shipment, unlock discounts, and get rewarded when you refer friends to D2D.',
      primary: { label: promo ? 'See This Promotion' : 'Create Account', to: '/register', href: promo ? '#promotions' : undefined },
      secondary: { label: 'View Promotions', href: '#promotions' },
      graphic: promo ? 'SAVE' : 'EARN',
    },
  ];
}

export default function Landing() {
  const [trackCode, setTrackCode] = useState('');
  const [promos, setPromos] = useState([]);
  const [activeSlide, setActiveSlide] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get('/api/promotions').then((r) => setPromos(r.data)).catch(() => {});
  }, []);

  const slides = useMemo(() => makeSlides(promos), [promos]);
  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % slides.length);
    }, 5000);

    return () => window.clearInterval(timer);
  }, [slides.length]);

  const stats = [
    { value: 'CN', label: 'China → Ghana', route: true, from: 'cn', to: 'gh' },
    { value: 'TR', label: 'Turkey → Ghana', route: true, from: 'tr', to: 'gh' },
    { value: '6', label: 'Tracking stages', icon: Route },
    { value: promos.length > 0 ? `${promos.length}` : 'LIVE', label: promos.length > 0 ? 'Active promotions' : 'Real-time updates', icon: Activity },
  ];

  const services = [
    { num: '01', title: 'Get your warehouse address', desc: 'After signup, you get a personal CN and TR warehouse address. Shop online and send items there.', badge: 'Free address' },
    { num: '02', title: 'We receive your packages', desc: 'When your items arrive at our warehouse in China or Turkey, you get notified instantly.', badge: 'Instant alerts' },
    { num: '03', title: 'Pay your shipping fee', desc: 'We calculate your fee by weight or size. Pay online or send a payment screenshot for review.', badge: 'Pay online' },
    { num: '04', title: 'Track your shipment', desc: 'Follow your items from the warehouse all the way to delivery at your door in Ghana.', badge: 'Live tracking' },
    { num: '05', title: 'Download your receipt', desc: 'Once payment is confirmed, your official receipt is ready to download anytime.', badge: 'PDF receipt' },
    { num: '06', title: 'Chat with our team', desc: 'Have a question? Our team is available through live chat inside the platform.', badge: 'Live support' },
  ];

  const handleTrack = (event) => {
    event.preventDefault();
    if (trackCode.trim()) navigate(`/track/${trackCode.trim()}`);
  };

  const goToSlide = (index) => setActiveSlide((index + slides.length) % slides.length);

  return (
    <div className="lp">
      <section className="slider">
        <div className="slides" style={{ transform: `translateX(-${activeSlide * 100}%)` }}>
          {slides.map((slide, index) => (
              <div key={slide.tag} className={`slide slide${index + 1}`}>
              <div className="slide-bg" style={{ backgroundImage: `url(${slide.image})` }} />
              <div className="slide-graphic" aria-hidden="true">{slide.graphic}</div>
              <div className="slide-content">
                <span className="slide-tag">{slide.tag}</span>
                <h1 className="slide-title">
                  {slide.titleTop}
                  <br />
                  <em>{slide.titleAccent}</em>
                  <br />
                  {slide.titleBottom}
                </h1>
                <p className="slide-desc">{slide.description}</p>
                <div className="slide-ctas">
                  {slide.primary.to ? (
                    <Link className="btn-primary slide-btn" to={slide.primary.to}>{slide.primary.label}</Link>
                  ) : (
                    <a className="btn-primary slide-btn" href={slide.primary.href}>{slide.primary.label}</a>
                  )}
                  <a className="btn-outline slide-btn" href={slide.secondary.href}>{slide.secondary.label}</a>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button type="button" className="arrow arrow-left" onClick={() => goToSlide(activeSlide - 1)} aria-label="Previous slide">
          <ChevronLeft />
        </button>
        <button type="button" className="arrow arrow-right" onClick={() => goToSlide(activeSlide + 1)} aria-label="Next slide">
          <ChevronRight />
        </button>

        <div className="slider-dots">
          {slides.map((slide, index) => (
            <button
              type="button"
              key={slide.tag}
              className={index === activeSlide ? 'dot active' : 'dot'}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      <section className="lp-track-hero" id="tracking">
        <div className="lp-track-hero-inner">
          <div className="lp-track-hero-text">
            <p className="lp-track-label">Track Your Package</p>
            <p className="lp-track-title">Where is my shipment?</p>
            <p className="lp-track-sub">Enter your tracking code or shipping mark to see the latest status.</p>
          </div>
          <form className="track-form lp-track-form" onSubmit={handleTrack}>
            <input
              className="track-input"
              type="text"
              placeholder="e.g. A1-D2D-SMITH or tracking code"
              value={trackCode}
              onChange={(event) => setTrackCode(event.target.value)}
            />
            <button type="submit" className="track-btn">Track Now</button>
          </form>
        </div>
      </section>

      <div className="stats-bar">
        {stats.map((stat) => (
          <div key={stat.label} className="stat-block">
            <div className="stat-icon">
              {stat.route ? <span className="stat-route-flags"><CountryFlag country={stat.from} /><b>→</b><CountryFlag country={stat.to} /></span> : typeof stat.icon === 'string' ? stat.icon : <stat.icon size={18} strokeWidth={2} />}
            </div>
            <div className="stat-num">{stat.value}</div>
            <div className="stat-label">{stat.label}</div>
          </div>
        ))}
      </div>

      <section className="section" id="services">
        <div className="section-label">How It Works</div>
        <div className="section-title-lg">SIX EASY STEPS<br />TO YOUR DOOR</div>
        <div className="section-sub">
          No experience needed. Create an account, get your warehouse address, and start shipping from China or Turkey to Ghana.
        </div>
        <div className="services-grid">
          {services.map((service) => (
            <div key={service.num} className="svc-box">
              <div className="svc-num">{service.num}</div>
              <div className="svc-title">{service.title}</div>
              <div className="svc-desc">{service.desc}</div>
              <span className="svc-badge">{service.badge}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="lp-cta-banner">
        <p className="lp-cta-title">Ready to start shipping?</p>
        <p className="lp-cta-sub">Create a free account and get your personal warehouse address in seconds.</p>
        <div className="lp-cta-btns">
          <Link to="/register" className="btn-primary slide-btn">Create Free Account</Link>
          <Link to="/login" className="lp-cta-signin">Already have an account? Sign in -&gt;</Link>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-brand">
          <div className="logo">D2D<span>SHIPPING PLATFORM</span></div>
          <p>Dock-to-door shipping from China and Turkey to Ghana. Track your packages, pay fees, and stay updated in one place.</p>
        </div>
        <div className="footer-col">
          <h4>For Customers</h4>
          <ul>
            <li><Link to="/register">Create account</Link></li>
            <li><a href="#tracking">Track a package</a></li>
            <li><Link to="/login">Sign in</Link></li>
            <li><a href="#promotions">View promotions</a></li>
          </ul>
        </div>
        <div className="footer-col">
          <h4>How It Works</h4>
          <ul>
            <li>Get warehouse address</li>
            <li>Ship items there</li>
            <li>Pay shipping fee</li>
            <li>Receive in Ghana</li>
          </ul>
        </div>
        <div className="footer-col">
          <h4>Routes</h4>
          <ul>
            <li>China to Ghana</li>
            <li>Turkey to Ghana</li>
            <li>Sea freight</li>
            <li>Air freight</li>
          </ul>
        </div>
      </footer>

      <div className="footer-bottom">
        <p>Copyright 2026 D2D Shipping. All rights reserved.</p>
        <p>
          China to Ghana | Turkey to Ghana | Shipping made simple | Built by{' '}
          <a href="https://smaiz.dev" target="_blank" rel="noreferrer">Smaiz Innovations</a>
        </p>
      </div>
    </div>
  );
}
